/* ============================================================================
 * useUndoHandlersInit.ts - Initialize undo/redo handlers
 * ============================================================================ */

import { useEffect, type Dispatch, type SetStateAction } from 'react';
import { undoStack } from '@/shared/commands/UndoStack';
import { UndoHandlers, type StateSetters } from '@/shared/commands/UndoHandlers';
import type { NodeOperation, ViewFilter } from '@/shared/types/projectData';

interface UseUndoHandlersInitParams {
  applyOp: (op: NodeOperation) => Promise<void>;
  setSelectedNodeIds: Dispatch<SetStateAction<Set<string>>>;
  setExpandedNodeIds: Dispatch<SetStateAction<Set<string>>>;
  setFilters: Dispatch<SetStateAction<ViewFilter>>;
  setSearchQueryState: Dispatch<SetStateAction<string>>;
  setSortByState: Dispatch<SetStateAction<string | null>>;
  setIsCompactViewState: Dispatch<SetStateAction<boolean>>;
}

/**
 * Hook to initialize undo/redo handlers and connect them to the undo stack
 */
export function useUndoHandlersInit({
  applyOp,
  setSelectedNodeIds,
  setExpandedNodeIds,
  setFilters,
  setSearchQueryState,
  setSortByState,
  setIsCompactViewState
}: UseUndoHandlersInitParams) {
  
  useEffect(() => {
    // Create state setters for undo handlers
    const stateSetters: StateSetters = {
      setSelectedNodeIds,
      setExpandedNodeIds,
      setFilters,
      setSearchQuery: setSearchQueryState,
      setSortBy: setSortByState,
      setIsCompactView: setIsCompactViewState,
      setActiveView: (view: string) => {
        if (typeof window === 'undefined') {
          return;
        }

        window.dispatchEvent(new CustomEvent('project-data:set-active-view', {
          detail: { view }
        }));
      }
    };

    // Initialize undo handlers
    const undoHandlers = new UndoHandlers(applyOp, stateSetters);
    undoStack.setUndoHandlers(undoHandlers);

    // REMOVED: updateUndoStateFromSnapshots() call that was causing infinite loop
    // This will now be called only after actual undo/redo operations
    
    // No need to subscribe to UndoStack since we're using SnapshotHistory as single source of truth
    return () => {}; // Return empty cleanup function
  }, [applyOp, setSelectedNodeIds, setExpandedNodeIds, setFilters, setSearchQueryState, 
      setSortByState, setIsCompactViewState]); // Removed updateUndoStateFromSnapshots to prevent infinite loop
}
